import React from 'react';
import type { Toast } from '../../hooks/useToast';

interface ToastContainerProps {
  toasts: Toast[];
  onRemove: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onRemove }) => {
  const getToastStyles = (type: Toast['type']) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200';
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-200';
      case 'info':
        return 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800 dark:bg-gray-900/20 dark:border-gray-800 dark:text-gray-200';
    }
  };

  const getIcon = (type: Toast['type']) => {
    switch (type) {
      case 'success':
        return 'ri-check-line';
      case 'error':
        return 'ri-close-line';
      case 'warning':
        return 'ri-alert-line';
      case 'info':
        return 'ri-information-line';
      default:
        return 'ri-notification-line';
    }
  };

  if (toasts.length === 0) return null;

  return (
    /* Toasts must outrank EVERY overlay in the app, because the messages that matter
     * most are raised from inside them. This was z-[100] while full-screen panels sit
     * at z-[9000]-z-[99999]: a server refusal fired, rendered underneath the panel that
     * covered it, and expired unseen. On Check Measurements the Approve button appeared
     * to do nothing at all, while a 409 explaining exactly why was arriving each click.
     *
     * 999999 is deliberately clear of the current ceiling (99999) so a new overlay does
     * not silently reintroduce the bug. Nothing should ever be layered above a toast. */
    <div className="fixed top-4 right-4 z-[999999] space-y-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`
            flex items-center p-4 rounded-lg border shadow-lg max-w-sm
            animate-in slide-in-from-right-full duration-300
            ${getToastStyles(toast.type)}
          `}
        >
          <i className={`${getIcon(toast.type)} mr-3 text-lg flex-shrink-0`}></i>
          <p className="text-sm font-medium flex-1">{toast.message}</p>
          <button
            onClick={() => onRemove(toast.id)}
            className="ml-2 text-current opacity-70 hover:opacity-100 transition-opacity"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
      ))}
    </div>
  );
};

export default ToastContainer;